import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Check } from "lucide-react";

const COLOR_DOT = {
  "Auburn": "#8B3A2A",
  "Blonde": "#E8C98A",
  "Burgundy": "#5C1A2B",
  "Copper": "#B87333",
  "Dark Brown": "#3B2414",
  "Honey Blonde": "#D8A663",
  "Jet Black": "#0B0B0B",
  "Natural Black": "#1A1410",
  "Platinum": "#E8E6DF",
};

const lengthInches = (s) => parseInt(String(s || "").replace(/[^0-9]/g, ""), 10) || 0;

// Pricing rules
const PRICE_PER_INCH = 10; // $20 per 2-inch step

// Lace size deltas (vs 13x4 baseline)
const LACE_SIZE_DELTA = {
  "5x5": -20,
  "7x5": -10,
  "13x4": 0,
  "13x6": 15,
  "360": 30,
};

// Lace quality deltas
const LACE_QUALITY_DELTA = {
  "Transparent": 10,
  "Swiss": 10,
  "French": 5,
  "HD": 15,
};

// Hair origin deltas (Indian baseline)
const ORIGIN_DELTA = {
  "Indian": 0,
  "Malaysian": 10,
  "Brazilian": 15,
};

// Density deltas (180% baseline)
const DENSITY_DELTA = {
  "180%": 0,
  "200%": 15,
  "250%": 30,
};

// Lace construction deltas (Lace Front baseline)
const CONSTRUCTION_DELTA = {
  "Lace Front Wig": 0,
  "360 Lace Wig": 20,
  "Full Lace Wig": 50,
};

// Closure / Frontal add-on for weave bundles
const CLOSURE_OPTIONS = [
  { key: "none", label: "Bundles only", delta: 0 },
  { key: "4x4", label: "+ 4x4 HD Closure", delta: 45 },
  { key: "5x5", label: "+ 5x5 HD Closure", delta: 60 },
  { key: "13x4", label: "+ 13x4 HD Frontal", delta: 80 },
  { key: "13x6", label: "+ 13x6 HD Frontal", delta: 100 },
];

// Bundle count options for weaves (minimum 3 — true "bundle deal")
const BUNDLE_OPTIONS = [
  { key: "3", label: "3 Bundles", multiplier: 2.7 },
  { key: "4", label: "4 Bundles", multiplier: 3.5 },
];

const computePrice = (basePrice, baseLength, selLength, baseSize, selSize, baseQuality, selQuality, baseOrigin, selOrigin, baseDensity, selDensity, baseConstruction, selConstruction) => {
  let price = basePrice;
  const baseIn = lengthInches(baseLength);
  const selIn = lengthInches(selLength);
  if (baseIn && selIn && baseIn !== selIn) {
    price += (selIn - baseIn) * PRICE_PER_INCH;
  }
  if (baseSize && selSize && baseSize !== selSize) {
    price += (LACE_SIZE_DELTA[selSize] ?? 0) - (LACE_SIZE_DELTA[baseSize] ?? 0);
  }
  if (baseQuality && selQuality && baseQuality !== selQuality) {
    price += (LACE_QUALITY_DELTA[selQuality] ?? 0) - (LACE_QUALITY_DELTA[baseQuality] ?? 0);
  }
  if (baseOrigin && selOrigin && baseOrigin !== selOrigin) {
    price += (ORIGIN_DELTA[selOrigin] ?? 0) - (ORIGIN_DELTA[baseOrigin] ?? 0);
  }
  if (baseDensity && selDensity && baseDensity !== selDensity) {
    price += (DENSITY_DELTA[selDensity] ?? 0) - (DENSITY_DELTA[baseDensity] ?? 0);
  }
  if (baseConstruction && selConstruction && baseConstruction !== selConstruction) {
    price += (CONSTRUCTION_DELTA[selConstruction] ?? 0) - (CONSTRUCTION_DELTA[baseConstruction] ?? 0);
  }
  const floor = basePrice * 0.6;
  return Math.max(floor, price);
};

const QuickViewDialog = ({ product, open, onOpenChange }) => {
  const [length, setLength] = useState("");
  const [color, setColor] = useState("");
  const [size, setSize] = useState(""); // lace size
  const [quality, setQuality] = useState(""); // lace type/material
  const [origin, setOrigin] = useState(""); // hair origin
  const [density, setDensity] = useState(""); // hair density
  const [hairline, setHairline] = useState(""); // hairline type
  const [construction, setConstruction] = useState(""); // lace construction
  const [activeImage, setActiveImage] = useState("");
  const [closure, setClosure] = useState("none"); // weave: closure/frontal add-on
  const [bundles, setBundles] = useState("3"); // weave: bundle count

  useEffect(() => {
    if (product) {
      setLength(product.length || product.length_options?.[0] || "");
      setColor(product.color || product.color_options?.[0] || "");
      setSize(product.lace_type || product.lace_type_options?.[0] || "");
      setQuality(product.lace_quality || product.lace_quality_options?.[0] || "");
      setOrigin(product.hair_origin || product.hair_origin_options?.[0] || "");
      setDensity(product.density || product.density_options?.[0] || "");
      setHairline(product.hairline || product.hairline_options?.[0] || "");
      setConstruction(product.lace_construction || product.lace_construction_options?.[0] || "");
      setActiveImage(product.image || product.images?.[0] || "");
      setClosure("none");
      setBundles("3");
    }
  }, [product]);

  const lengthOptions = useMemo(() => {
    const arr = product?.length_options?.length ? product.length_options : (product?.length ? [product.length] : []);
    return [...arr].sort((a, b) => lengthInches(a) - lengthInches(b));
  }, [product]);
  const colorOptions = product?.color_options?.length ? product.color_options : (product?.color ? [product.color] : []);
  const sizeOptions = product?.lace_type_options?.length ? product.lace_type_options : (product?.lace_type ? [product.lace_type] : []);
  const qualityOptions = product?.lace_quality_options?.length ? product.lace_quality_options : (product?.lace_quality ? [product.lace_quality] : []);
  const originOptions = product?.hair_origin_options?.length ? product.hair_origin_options : (product?.hair_origin ? [product.hair_origin] : []);
  const densityOptions = product?.density_options?.length ? product.density_options : (product?.density ? [product.density] : []);
  const hairlineOptions = product?.hairline_options?.length ? product.hairline_options : (product?.hairline ? [product.hairline] : []);
  const constructionOptions = product?.lace_construction_options?.length ? product.lace_construction_options : (product?.lace_construction ? [product.lace_construction] : []);

  if (!product) return null;
  const isWeave = product.category === "Weave";
  const isWig = !product.category || product.category === "Wig";
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const customized =
    length !== (product.length || "") ||
    color !== (product.color || "") ||
    size !== (product.lace_type || "") ||
    quality !== (product.lace_quality || "") ||
    origin !== (product.hair_origin || "") ||
    density !== (product.density || "") ||
    hairline !== (product.hairline || "") ||
    construction !== (product.lace_construction || "") ||
    (isWeave && (closure !== "none" || bundles !== "1"));

  const baseLivePrice = computePrice(
    product.price, product.length, length, product.lace_type, size, product.lace_quality, quality,
    product.hair_origin, origin, product.density, density,
    product.lace_construction, construction
  );
  const livePrice = (() => {
    if (!isWeave) return baseLivePrice;
    const bundle = BUNDLE_OPTIONS.find((b) => b.key === bundles) || BUNDLE_OPTIONS[0];
    const closureDelta = (CLOSURE_OPTIONS.find((c) => c.key === closure) || CLOSURE_OPTIONS[0]).delta;
    return baseLivePrice * bundle.multiplier + closureDelta;
  })();
  const liveCompare = product.compare_at_price
    ? (() => {
        const base = computePrice(product.compare_at_price, product.length, length, product.lace_type, size, product.lace_quality, quality,
          product.hair_origin, origin, product.density, density, product.lace_construction, construction);
        if (!isWeave) return base;
        const bundle = BUNDLE_OPTIONS.find((b) => b.key === bundles) || BUNDLE_OPTIONS[0];
        const closureDelta = (CLOSURE_OPTIONS.find((c) => c.key === closure) || CLOSURE_OPTIONS[0]).delta;
        return base * bundle.multiplier + closureDelta;
      })()
    : null;

  const buildShopUrl = () => {
    try {
      const u = new URL(product.shopify_url);
      if (length) u.searchParams.set("length", length);
      if (color) u.searchParams.set("color", color);
      if (size) u.searchParams.set("lace_size", size);
      if (quality) u.searchParams.set("lace_type", quality);
      if (origin) u.searchParams.set("origin", origin);
      if (density) u.searchParams.set("density", density);
      if (hairline) u.searchParams.set("hairline", hairline);
      if (construction) u.searchParams.set("construction", construction);
      if (isWeave) {
        u.searchParams.set("bundles", bundles);
        u.searchParams.set("closure", closure);
      }
      u.searchParams.set("price", livePrice.toFixed(2));
      return u.toString();
    } catch {
      return product.shopify_url;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quick-view-dialog"
        className="max-w-3xl max-h-[92vh] overflow-y-auto bg-background p-0 rounded-sm"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{product.title}</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2">
          {/* Image gallery */}
          <div className="relative bg-secondary overflow-hidden flex flex-col">
            <div className="relative aspect-[4/5] md:aspect-[5/6]">
              <img
                key={activeImage}
                src={activeImage || product.image}
                alt={product.title}
                className="w-full h-full object-cover fade-up"
              />
              {onSale && (
                <Badge className="absolute top-4 left-4 rounded-sm bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] uppercase tracking-widest text-[10px] font-semibold">
                  Sale
                </Badge>
              )}
            </div>
            {(() => {
              const gallery = (product.images && product.images.length > 0)
                ? product.images
                : (product.image ? [product.image] : []);
              if (gallery.length <= 1) return null;
              return (
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 p-3 bg-background/60">
                  {gallery.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      data-testid={`qv-thumb-${idx}`}
                      onClick={() => setActiveImage(img)}
                      className={[
                        "aspect-square rounded-sm overflow-hidden border-2 transition-all",
                        (activeImage || product.image) === img
                          ? "border-[hsl(var(--primary))]"
                          : "border-transparent opacity-70 hover:opacity-100",
                      ].join(" ")}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Info */}
          <div className="p-6 lg:p-8 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#B8935F] font-medium">
              Kay's Beauty Hair
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-2 leading-tight text-foreground">
              {product.title}
            </h2>

            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              {onSale && liveCompare && (
                <span className="text-sm text-muted-foreground line-through">
                  ${liveCompare.toFixed(2)}
                </span>
              )}
              <span className={`text-2xl font-semibold ${onSale ? "text-[hsl(var(--primary))]" : "text-foreground"}`}>
                ${livePrice.toFixed(2)}
              </span>
              {isWig && customized && (
                <span className="text-[10px] uppercase tracking-widest text-[#B8935F] ml-1">
                  for {length} · {quality}
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">
              Shipping & taxes calculated at checkout
            </div>

            {product.description && (
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Weave-only: Bundle count + Closure / Frontal add-on */}
            {isWeave && (
              <>
                <div className="mt-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Bundles</div>
                    <div className="text-xs text-muted-foreground">
                      {(BUNDLE_OPTIONS.find((b) => b.key === bundles) || BUNDLE_OPTIONS[0]).label}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {BUNDLE_OPTIONS.map((b) => {
                      const active = b.key === bundles;
                      return (
                        <button
                          key={b.key}
                          data-testid={`qv-bundles-${b.key}`}
                          onClick={() => setBundles(b.key)}
                          className={[
                            "h-10 px-3 text-xs font-medium rounded-sm border transition-all",
                            active
                              ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                              : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                          ].join(" ")}
                        >
                          {b.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Add closure or frontal</div>
                    <div className="text-xs text-muted-foreground">
                      {(CLOSURE_OPTIONS.find((c) => c.key === closure) || CLOSURE_OPTIONS[0]).label}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {CLOSURE_OPTIONS.map((c) => {
                      const active = c.key === closure;
                      return (
                        <button
                          key={c.key}
                          data-testid={`qv-closure-${c.key}`}
                          onClick={() => setClosure(c.key)}
                          className={[
                            "h-10 px-2 text-[11px] font-medium rounded-sm border transition-all flex flex-col items-center justify-center leading-tight",
                            active
                              ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                              : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                          ].join(" ")}
                        >
                          <span>{c.label}</span>
                          {c.delta > 0 && !active && (
                            <span className="text-[9px] opacity-70">+${c.delta}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Length */}
            {isWig && lengthOptions.length > 0 && (
              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Length</div>
                  <div className="text-xs text-muted-foreground">{length}</div>
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {lengthOptions.map((l) => {
                    const active = l === length;
                    return (
                      <button
                        key={l}
                        data-testid={`qv-length-${l}`}
                        onClick={() => setLength(l)}
                        className={[
                          "h-9 text-xs font-medium rounded-sm border transition-all",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Color */}
            {isWig && colorOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Color</div>
                  <div className="text-xs text-muted-foreground">{color}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((c) => {
                    const active = c === color;
                    const dot = COLOR_DOT[c] || "#999";
                    return (
                      <button
                        key={c}
                        data-testid={`qv-color-${c}`}
                        onClick={() => setColor(c)}
                        title={c}
                        className={[
                          "relative w-9 h-9 rounded-full transition-all border",
                          active ? "border-[hsl(var(--primary))]" : "border-border/70 hover:border-foreground/40",
                        ].join(" ")}
                        style={{ background: dot, boxShadow: active ? "0 0 0 2px #B8935F" : "none" }}
                        aria-label={c}
                      >
                        {active && (
                          <Check className="w-3.5 h-3.5 text-white absolute inset-0 m-auto" strokeWidth={3} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lace Size */}
            {isWig && sizeOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Lace Size</div>
                  <div className="text-xs text-muted-foreground">{size}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sizeOptions.map((l) => {
                    const active = l === size;
                    const delta = (LACE_SIZE_DELTA[l] ?? 0) - (LACE_SIZE_DELTA[product.lace_type] ?? 0);
                    return (
                      <button
                        key={l}
                        data-testid={`qv-lace-size-${l}`}
                        onClick={() => setSize(l)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all flex items-center gap-1",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {l}
                        {delta !== 0 && !active && (
                          <span className="text-[9px] opacity-70">{delta > 0 ? `+$${delta}` : `−$${Math.abs(delta)}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lace Type (material) */}
            {isWig && qualityOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Lace Type</div>
                  <div className="text-xs text-muted-foreground">{quality}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {qualityOptions.map((q) => {
                    const active = q === quality;
                    const delta = (LACE_QUALITY_DELTA[q] ?? 0) - (LACE_QUALITY_DELTA[product.lace_quality] ?? 0);
                    return (
                      <button
                        key={q}
                        data-testid={`qv-lace-type-${q}`}
                        onClick={() => setQuality(q)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all flex items-center gap-1",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {q}
                        {delta !== 0 && !active && (
                          <span className="text-[9px] opacity-70">{delta > 0 ? `+$${delta}` : `−$${Math.abs(delta)}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Hair Origin */}
            {isWig && originOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Hair Origin</div>
                  <div className="text-xs text-muted-foreground">{origin}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {originOptions.map((o) => {
                    const active = o === origin;
                    const delta = (ORIGIN_DELTA[o] ?? 0) - (ORIGIN_DELTA[product.hair_origin] ?? 0);
                    return (
                      <button
                        key={o}
                        data-testid={`qv-origin-${o}`}
                        onClick={() => setOrigin(o)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all flex items-center gap-1",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {o}
                        {delta !== 0 && !active && (
                          <span className="text-[9px] opacity-70">{delta > 0 ? `+$${delta}` : `−$${Math.abs(delta)}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Density */}
            {isWig && densityOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Density</div>
                  <div className="text-xs text-muted-foreground">{density}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {densityOptions.map((d) => {
                    const active = d === density;
                    const delta = (DENSITY_DELTA[d] ?? 0) - (DENSITY_DELTA[product.density] ?? 0);
                    return (
                      <button
                        key={d}
                        data-testid={`qv-density-${d}`}
                        onClick={() => setDensity(d)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all flex items-center gap-1",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {d}
                        {delta !== 0 && !active && (
                          <span className="text-[9px] opacity-70">{delta > 0 ? `+$${delta}` : `−$${Math.abs(delta)}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Hairline */}
            {isWig && hairlineOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Hairline</div>
                  <div className="text-xs text-muted-foreground">{hairline}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {hairlineOptions.map((h) => {
                    const active = h === hairline;
                    return (
                      <button
                        key={h}
                        data-testid={`qv-hairline-${h}`}
                        onClick={() => setHairline(h)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {h}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lace Construction */}
            {isWig && constructionOptions.length > 0 && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/70 font-semibold">Lace Construction</div>
                  <div className="text-xs text-muted-foreground">{construction}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {constructionOptions.map((c) => {
                    const active = c === construction;
                    const delta = (CONSTRUCTION_DELTA[c] ?? 0) - (CONSTRUCTION_DELTA[product.lace_construction] ?? 0);
                    return (
                      <button
                        key={c}
                        data-testid={`qv-construction-${c}`}
                        onClick={() => setConstruction(c)}
                        className={[
                          "h-9 px-3 text-xs font-medium rounded-sm border transition-all flex items-center gap-1",
                          active
                            ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                            : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                        ].join(" ")}
                      >
                        {c}
                        {delta !== 0 && !active && (
                          <span className="text-[9px] opacity-70">{delta > 0 ? `+$${delta}` : `−$${Math.abs(delta)}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isWig && customized && (
              <div className="mt-5 bg-secondary/50 border border-border/60 rounded-sm px-3 py-2 text-xs text-foreground/80">
                Your selection: <span className="font-medium">{length}</span> · <span className="font-medium">{color}</span> · <span className="font-medium">{size}</span> · <span className="font-medium">{quality}</span> · <span className="font-medium">{origin}</span> · <span className="font-medium">{density}</span> · <span className="font-medium">{hairline}</span> · <span className="font-medium">{construction}</span>. We'll match it at checkout.
              </div>
            )}

            {/* CTA */}
            <div className="mt-auto pt-6">
              <Button
                data-testid="qv-shop-cta"
                asChild
                className="w-full rounded-sm h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))]"
              >
                <a href={buildShopUrl()} target="_blank" rel="noopener noreferrer">
                  Shop on Kay's <ExternalLink className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <p className="text-[11px] text-center text-muted-foreground mt-2">
                Secure checkout on our Shopify store
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewDialog;
